import { useEffect } from 'react';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';

const getState = () => {
  if (document.visibilityState === 'hidden') {
    return 'hidden';
  }
  if (document.hasFocus()) {
    return 'active';
  }
  return 'passive';
};

type windowState = 'active' | 'passive' | 'hidden' | 'frozen' | 'terminated' | 'discarded';

const setWindowState = (state: windowState) => {
  analytics.addViewContext(globalContext.viewer.windowState, state);
};

const getStateFromWindow = () => setWindowState(getState());

const setWindowIsFrozen = () => {
  // In the freeze event, the next state is always frozen.
  setWindowState('frozen');
};

const handlePageHide = (event: PageTransitionEvent) => {
  // If the event's persisted property is `true` the page is about
  // to enter the back/forward cache, which is also in the frozen state.
  // If the event's persisted property is not `true` the page is
  // about to be unloaded.
  setWindowState(event.persisted ? 'frozen' : 'terminated');
};

export const useWindowStateAnalytics = (): void => {
  const opts = { capture: true };

  useEffect(() => {
    // Set the initial state
    getStateFromWindow();
    //@ts-expect-error exists as of Chrome 68: https://developer.chrome.com/docs/web-platform/page-lifecycle-api
    if (document.wasDiscarded === true) {
      analytics.addViewContext(globalContext.viewer.windowState, 'discarded');
    }
    // These lifecycle events can all use the same listener to observe state
    // changes (they call the `getState()` function to determine the next state).
    ['pageshow', 'focus', 'blur', 'visibilitychange', 'resume'].forEach((type) => {
      window.addEventListener(type, getStateFromWindow, opts);
    });

    // The next two listeners, on the other hand, can determine the next
    // state from the event itself.
    window.addEventListener('freeze', setWindowIsFrozen, opts);

    window.addEventListener('pagehide', handlePageHide, opts);
    return () => {
      // Cleanup all listeners
      ['pageshow', 'focus', 'blur', 'visibilitychange', 'resume'].forEach((type) => {
        window.removeEventListener(type, getStateFromWindow, opts);
      });
      window.removeEventListener('freeze', setWindowIsFrozen, opts);
      window.removeEventListener('pagehide', handlePageHide, opts);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
